import { useToast } from '@arnold/common';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDefinition } from '../ReportDefinitionContext';
import { EditorModal } from './EditorModal';
import { Spacer } from './editorStyles';
import { Action, Check, ErrorText, Field, FormSection, Hint } from './styles';

/** Saves the draft for this report, or connects it to the survey's topic, then leaves edit mode. */
export function SaveModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation('newReport');
  const { addToast } = useToast();
  const controller = useDefinition();
  const [name, setName] = useState('');
  const [primaryForSurvey, setPrimaryForSurvey] = useState(true);
  const [primaryForTopic, setPrimaryForTopic] = useState(false);
  const [busy, setBusy] = useState(false);
  const submitSurvey = async () => {
    setBusy(true);
    const id = await controller.saveForSurvey(name);
    if (id && primaryForSurvey) await controller.setPrimaryForSurvey(id);
    setBusy(false);
    if (!id) return;
    addToast(t(primaryForSurvey ? 'definition.savedPrimaryForSurvey' : 'definition.savedNotPrimaryForSurvey'));
    controller.close();
    onClose();
  };
  const submitTopic = async () => {
    setBusy(true);
    const id = await controller.saveForTopic(name, primaryForTopic);
    setBusy(false);
    if (!id) return;
    addToast(t(primaryForTopic ? 'definition.savedPrimaryForTopic' : 'definition.savedForTopic'));
    controller.close();
    onClose();
  };
  return (
    <EditorModal
      title={t('definition.saveTitle')}
      onClose={onClose}
      footer={
        <>
          <Spacer />
          <Action disabled={busy} onClick={onClose}>
            {t('definition.cancel')}
          </Action>
          <Action primary disabled={busy || controller.busy || !!controller.validationError} onClick={submitSurvey}>
            {t('definition.saveVersion')}
          </Action>
        </>
      }
    >
      <FormSection style={{ padding: 0, border: 0 }}>
        <Hint>{t('definition.saveHelp')}</Hint>
        <Field>
          {t('definition.versionName')}
          <input maxLength={200} value={name} autoFocus onChange={(event) => setName(event.target.value)} />
        </Field>
        <Check>
          <input
            type="checkbox"
            checked={primaryForSurvey}
            onChange={(event) => setPrimaryForSurvey(event.target.checked)}
          />
          {t('definition.makePrimaryAfterSave')}
        </Check>
        <Hint>{t('definition.saveForTopicHelp')}</Hint>
        <Check>
          <input
            type="checkbox"
            checked={primaryForTopic}
            onChange={(event) => setPrimaryForTopic(event.target.checked)}
          />
          {t('definition.primaryForTopic')}
        </Check>
        <Action disabled={busy || controller.busy || !!controller.validationError} onClick={submitTopic}>
          {t('definition.saveForTopic')}
        </Action>
        {controller.validationError && (
          <ErrorText>
            {t('definition.invalid', { reason: t(`definition.validation.${controller.validationError}`) })}
          </ErrorText>
        )}
        {controller.error && <ErrorText>{controller.error}</ErrorText>}
      </FormSection>
    </EditorModal>
  );
}
